import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {{ x: number; y: number } | null} */
  #swipeStart = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleCloseButtonClick);
    this.addEventListener('pointerup', this.#handleCloseButtonClick);
    this.addEventListener('click', this.#handleOverlayClick);
    this.addEventListener('pointerdown', this.#handleSwipeStart);
    this.addEventListener('pointerup', this.#handleSwipeEnd);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleCloseButtonClick);
    this.removeEventListener('pointerup', this.#handleCloseButtonClick);
    this.removeEventListener('click', this.#handleOverlayClick);
    this.removeEventListener('pointerdown', this.#handleSwipeStart);
    this.removeEventListener('pointerup', this.#handleSwipeEnd);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;

    if (this.hasAttribute('auto-open')) {
      requestAnimationFrame(() => {
        this.showDialog();
        this.#announceCartCount(event.detail.resource?.item_count);
      });

      return;
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Keeps the drawer close button reliable after cart section hydration on touch devices.
   * @param {MouseEvent | PointerEvent} event
   */
  #handleCloseButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const closeButton = event.target.closest(
      '.cart-drawer__close-button, .cart-drawer__mobile-dismiss, .cart-drawer__swipe-dismiss'
    );
    if (!closeButton || !this.contains(closeButton)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#closeCartDrawer();
  };

  /**
   * Lets shoppers tap the dimmed area beside the mobile drawer to escape.
   * @param {MouseEvent} event
   */
  #handleOverlayClick = (event) => {
    if (event.target !== this.refs.dialog) return;
    this.#closeCartDrawer();
  };

  /**
   * Tracks horizontal swipes that start inside the drawer.
   * @param {PointerEvent} event
   */
  #handleSwipeStart = (event) => {
    if (!isMobileBreakpoint() || !this.refs.dialog?.open) return;
    if (!(event.target instanceof Element)) return;

    const drawerInner = event.target.closest('.cart-drawer__inner');
    if (!drawerInner || !this.contains(drawerInner)) return;

    this.#swipeStart = { x: event.clientX, y: event.clientY };
  };

  /**
   * Closes the right-side drawer when swiped to the right.
   * @param {PointerEvent} event
   */
  #handleSwipeEnd = (event) => {
    if (!this.#swipeStart) return;

    const deltaX = event.clientX - this.#swipeStart.x;
    const deltaY = Math.abs(event.clientY - this.#swipeStart.y);
    this.#swipeStart = null;

    if (deltaX > 65 && deltaX > deltaY * 1.4) {
      this.#closeCartDrawer();
    }
  };

  /**
   * Keeps Escape reliable even if focus lands inside hydrated cart content.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.#closeCartDrawer();
  };

  async #closeCartDrawer() {
    if (!this.refs.dialog?.open) return;

    try {
      await this.closeDialog();
    } catch (_error) {
      this.refs.dialog.close();
    }
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.#closeCartDrawer();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
